import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Workstream } from '../types/workstream';
import type { Task, ListTasksResponse } from '../types/task';
import type { TaskFilterState } from '../types/taskFilter';
import EditableTitle from './EditableTitle';
import EditableDescription from './EditableDescription';
import WorkstreamCardActions from './WorkstreamCardActions';
import SwimLanes from './SwimLanes';
import useTaskPolling from '../hooks/useTaskPolling';

interface WorkstreamCardProps {
  workstream: Workstream;
  onWorkstreamChanged: () => void;
  onVisibilityChange?: (wsID: string, visible: boolean) => void;
  taskFilter?: TaskFilterState;
  userTenantID?: string;
}

function getStatusColor(ws: Workstream): string {
  if (ws.deleted) return '#cf222e';   // red
  if (ws.paused) return '#dbab09';    // yellow
  return '#2da44e';                   // green
}

function getHeaderBg(ws: Workstream): string {
  if (ws.deleted) return '#ffcdd2';   // light red
  if (ws.paused) return '#fff3b0';    // light yellow
  return '#a6d89b';                   // light green
}

export default function WorkstreamCard({ workstream, onWorkstreamChanged, onVisibilityChange, taskFilter, userTenantID }: WorkstreamCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskErrors, setTaskErrors] = useState<string[]>([]);
  const tasksRef = useRef<Task[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const loadedRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  // IntersectionObserver for visibility tracking + parent callback
  useEffect(() => {
    if (!rootRef.current) return;
    const el = rootRef.current;
    const obs = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
        onVisibilityChange?.(workstream.workstreamID, entry.isIntersecting);
      },
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [workstream.workstreamID, onVisibilityChange]);

  // Poll for task updates — only after initial load completes and when
  // expanded and visible, to avoid racing with mount-time pagination
  // and hammering the API for off-screen or collapsed cards.
  useTaskPolling(workstream.workstreamID, tasksRef, setTasks, initialLoadDone && !collapsed && visible);

  // Load tasks on mount (first page)
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    const wsID = workstream.workstreamID;
    const baseUrl = `/api/workstreams/${encodeURIComponent(wsID)}/tasks?maxResults=200&includeDeleted=true`;
    (async () => {
      const all: Task[] = [];
      let url = baseUrl;
      while (url) {
        const resp = await fetch(url);
        if (cancelled || !resp.ok) return;
        const data: ListTasksResponse = await resp.json();
        const page = data.Tasks ?? [];
        // Dedupe as we append
        for (const t of page) {
          if (!all.some((x) => x.TaskID === t.TaskID)) all.push(t);
        }
        if (!cancelled) setTasks([...all]);
        if (data.NextToken) {
          url = `${baseUrl}&token=${encodeURIComponent(data.NextToken)}`;
        } else {
          url = '';
        }
      }
      if (!cancelled) setInitialLoadDone(true);
    })().catch(() => {});
    return () => { cancelled = true; };
  }, [workstream.workstreamID]);

  const wsUrl = `/api/workstreams/${encodeURIComponent(workstream.workstreamID)}`;

  const handleSaveTitle = useCallback(async (newTitle: string) => {
    const resp = await fetch(wsUrl, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Version: workstream.version, Name: newTitle }),
    });
    if (!resp.ok) throw new Error(await resp.text());
    onWorkstreamChanged();
  }, [wsUrl, workstream.version, onWorkstreamChanged]);

  const handleSaveDescription = useCallback(async (newDesc: string) => {
    const resp = await fetch(wsUrl, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Version: workstream.version, Description: newDesc }),
    });
    if (!resp.ok) throw new Error(await resp.text());
    onWorkstreamChanged();
  }, [wsUrl, workstream.version, onWorkstreamChanged]);

  const refetchTasks = useCallback(async () => {
    const baseUrl = `/api/workstreams/${encodeURIComponent(workstream.workstreamID)}/tasks?maxResults=200&includeDeleted=true`;
    const all: Task[] = [];
    let url = baseUrl;
    while (url) {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`refetch failed: ${resp.status}`);
      const data: ListTasksResponse = await resp.json();
      all.push(...(data.Tasks ?? []));
      url = data.NextToken ? `${baseUrl}&token=${encodeURIComponent(data.NextToken)}` : '';
    }
    setTasks(all);
  }, [workstream.workstreamID]);

  const handleTaskChanged = useCallback((updated: Task) => {
    const existing = tasksRef.current.find((t) => t.TaskID === updated.TaskID);
    const deletedChanged = existing && !!existing.Deleted !== !!updated.Deleted;
    setTasks((prev) => prev.map((t) => (t.TaskID === updated.TaskID ? { ...t, ...updated } : t)));
    if (deletedChanged) refetchTasks().catch(() => {});
  }, [refetchTasks]);

  const handleCreateTask = useCallback(async () => {
    const resp = await fetch(`/api/workstreams/${encodeURIComponent(workstream.workstreamID)}/tasks`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Title: 'New Task', Parallel: false }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      setTaskErrors((prev) => [...prev, text || 'Failed to create task']);
      return;
    }
    const newTask: Task = await resp.json();
    setTasks((prev) => {
      if (prev.some((t) => t.TaskID === newTask.TaskID)) return prev;
      return [...prev, newTask];
    });
  }, [workstream.workstreamID]);

  const handleReorderTask = useCallback(async (
    taskID: string,
    anchor: { kind: 'after' | 'before'; targetTaskID: string },
  ) => {
    const snapshot = tasksRef.current;
    const fromIdx = snapshot.findIndex((t) => t.TaskID === taskID);
    const targetIdx = snapshot.findIndex((t) => t.TaskID === anchor.targetTaskID);
    if (fromIdx === -1 || targetIdx === -1) return;
    const version = snapshot[fromIdx].Version ?? 0;

    // Optimistic reorder
    setTasks((prev) => {
      const next = prev.slice();
      const fi = next.findIndex((t) => t.TaskID === taskID);
      const ti = next.findIndex((t) => t.TaskID === anchor.targetTaskID);
      if (fi === -1 || ti === -1) return prev;
      const [moved] = next.splice(fi, 1);
      const insertIdx = anchor.kind === 'after' ? (fi < ti ? ti : ti + 1) : (fi < ti ? ti - 1 : ti);
      next.splice(Math.max(0, insertIdx), 0, moved);
      return next;
    });

    const fields: Record<string, string> = {};
    if (anchor.kind === 'after') fields.AfterTaskID = anchor.targetTaskID;
    else fields.BeforeTaskID = anchor.targetTaskID;

    try {
      const resp = await fetch(
        `/api/workstreams/${encodeURIComponent(workstream.workstreamID)}/tasks/${encodeURIComponent(taskID)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Version: version, Fields: fields }),
        },
      );
      if (!resp.ok) {
        await refetchTasks().catch(() => setTasks(snapshot));
        return;
      }
      const updated: Task = await resp.json();
      setTasks((prev) => prev.map((t) => (t.TaskID === updated.TaskID ? { ...t, ...updated } : t)));
    } catch {
      await refetchTasks().catch(() => setTasks(snapshot));
    }
  }, [workstream.workstreamID, refetchTasks]);

  const handleChangeTaskState = useCallback(async (taskID: string, newState: string) => {
    const snapshot = tasksRef.current;
    const task = snapshot.find((t) => t.TaskID === taskID);
    if (!task || (task.State ?? '') === newState) return;
    const version = task.Version ?? 0;

    setTasks((prev) => prev.map((t) => (t.TaskID === taskID ? { ...t, State: newState } : t)));

    try {
      const resp = await fetch(
        `/api/workstreams/${encodeURIComponent(workstream.workstreamID)}/tasks/${encodeURIComponent(taskID)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Version: version, Fields: { State: newState, AutoPause: true } }),
        },
      );
      if (!resp.ok) {
        await refetchTasks().catch(() => setTasks(snapshot));
        return;
      }
      const updated: Task = await resp.json();
      setTasks((prev) => prev.map((t) => (t.TaskID === updated.TaskID ? { ...t, ...updated } : t)));
      if (task.AssignedToAI) onWorkstreamChanged();
    } catch {
      await refetchTasks().catch(() => setTasks(snapshot));
    }
  }, [workstream.workstreamID, refetchTasks, onWorkstreamChanged]);

  const pushError = useCallback((msg: string) => {
    setTaskErrors((prev) => [...prev, msg]);
  }, []);

  const statusColor = getStatusColor(workstream);
  const headerBg = getHeaderBg(workstream);
  const visibleTasks = useMemo(() => {
    let filtered = tasks;
    // Archived filter
    if (!taskFilter?.showArchivedTasks) {
      filtered = filtered.filter((t) => !t.Deleted);
    }
    // Assignee filter (empty set = show all)
    if (taskFilter && taskFilter.assignees.size > 0) {
      filtered = filtered.filter((t) => {
        if (taskFilter.assignees.has('ai') && t.AssignedToAI) return true;
        if (taskFilter.assignees.has('user') && !t.AssignedToAI && t.AssignedToTenantID === userTenantID) return true;
        if (taskFilter.assignees.has('unassigned') && !t.AssignedToAI && !t.AssignedToTenantID) return true;
        return false;
      });
    }
    // Text search (title + prompt)
    if (taskFilter?.search) {
      const q = taskFilter.search.toLowerCase();
      filtered = filtered.filter((t) =>
        (t.Title ?? '').toLowerCase().includes(q) ||
        (t.Prompt ?? '').toLowerCase().includes(q),
      );
    }
    return filtered;
  }, [tasks, taskFilter, userTenantID]);

  return (
    <div
      ref={rootRef}
      style={{
        border: `2px solid ${statusColor}`,
        borderRadius: '8px',
        marginBottom: '16px',
        overflow: 'hidden',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 12px',
          backgroundColor: headerBg,
          gap: '8px',
          minHeight: '36px',
        }}
      >
        {/* Chevron */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand' : 'Collapse'}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            lineHeight: 1,
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#24292f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {/* Title */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <EditableTitle title={workstream.name} onSave={handleSaveTitle} />
        </div>

        {/* Action buttons */}
        <WorkstreamCardActions workstream={workstream} onWorkstreamChanged={onWorkstreamChanged} />
      </div>

      {/* Collapsible body */}
      {!collapsed && (
        <div style={{ padding: '8px 12px' }}>
          <EditableDescription description={workstream.description} onSave={handleSaveDescription} />

          {taskErrors.length > 0 && (
            <div style={{ margin: '8px 0' }}>
              {taskErrors.map((err, i) => (
                <div key={i} style={{ padding: '4px 8px', background: '#ffebe9', border: '1px solid #cf222e', borderRadius: '4px', color: '#cf222e', fontSize: '13px', marginBottom: '4px' }}>
                  {err}
                  <button type="button" onClick={() => setTaskErrors((prev) => prev.filter((_, j) => j !== i))} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>&times;</button>
                </div>
              ))}
            </div>
          )}

          <SwimLanes
            tasks={visibleTasks}
            defaultShortName={workstream.defaultShortName}
            onTaskChanged={handleTaskChanged}
            onError={pushError}
            onWorkstreamChanged={onWorkstreamChanged}
            onCreateTask={handleCreateTask}
            onReorderTask={handleReorderTask}
            onChangeTaskState={handleChangeTaskState}
          />
        </div>
      )}
    </div>
  );
}
